<template lang="pug">
.exchange-container
  //- 头部标题
  .exchange-header
    .header-left
      span.header-title {{ moduleConfig.mainTitle }}
      //- 图标 + 副标题（富文本），subtitleLogoSwitch 控制图标渲染
      .header-subtitle
        img.subtitle-logo(
          v-if="moduleConfig.subtitleLogoSwitch && moduleConfig.subtitleLogo"
          :src="moduleConfig.subtitleLogo"
        )
        HighlightText.subtitle-text(
          v-if="moduleConfig.subtitle"
          :text="moduleConfig.subtitle"
          :normalStyle="subtitleNormalStyle"
          :highlightStyle="subtitleHighlightStyle"
        )
    .my-coupon-btn(@click="goMyCoupon")
      span 我兑换的券
      img.arrow-icon(
        src="https://m.hellobike.com/15e7879a35.png"
        alt=""
      )

  //- 商品tabs
  .exchange-tabs
    .tabs
      .tab(
        v-for="(item, index) in tabs"
        :key="index"
        @click="changeTab(index)"
        :class="{ active: index === active }"
      ) {{ item.name }}

  //- 商品列表
  .exchange-content
    quark-list(
      @load="onLoad"
      :loading="loading"
      :finished="!hasMore[active]"
      :finishedtext="finishedtext"
      :offset="1"
    )
      div(slot="content")
        .exchange-items(v-if="items && items.length > 0")
          .exchange-item-card-area(v-for="(item, index) in items")
            ExchangeItemCard(
              :key="item.skuId"
              v-platform-expose="{ handler: itemExpose, data: { item, index } }"
              :item="item"
              :coinIcon="coinIcon"
              :hideBonus="hideBonus"
              @detail="goDetail($event, index)"
              @exchange="onExchange"
            )
        Empty(v-if="empty")
  .exchange-expose-line(v-platform-expose="moduleExpose")

  //- 兑换弹窗
  ExchangePopup(
    :visible="exchangePopupVisible"
    :exchangeInfo="currentExchangeInfo"
    :points="points"
    :isShowGroupPayment="isShowGroupPayment"
    :popupSource="popupSource"
    @close="handleExchangePopupClose"
    @success="handleExchangeSuccess"
    @showExchangeSuccessPopup="handleShowExchangeSuccessPopup"
    @bounty="$emit('bounty')"
    @earnPoints="handleEarnPoints"
  )

  //- 兑换成功弹窗
  ExchangeSuccessPopup(
    :visible="exchangeSuccessPopupVisible"
    :goodsInfo="currentExchangeInfo"
    @close="handleExchangeSuccessPopupClose"
    @showInfoClick="handleExchangeGoodsInfoClick"
  )
</template>

<script>
import Empty from '@/components/empty';
import { HighlightText } from '@hb/platformui-h5';
import { jumpUrl } from '@/utils/tools';
import Toast from '@/components/toast';
import config from '@/const/config';
import {
  SUBTITLE_NORMAL_STYLE, SUBTITLE_HIGHLIGHT_STYLE, TABS, FULI_MODULE,
} from '../../consts';
import ExchangeItemCard from '../../components/exchange-item-card';
import SPM from '../../common/spm';
import ExchangePopup from './exchange-popup';
import ExchangeSuccessPopup from './exchange-success-popup';
import { getExchangeList, welfareGoCoupon } from '../../utils';
import 'quarkd/lib/list';

const mapper = ({
  itemName,
  virtualSalePrice,
  listItemUrl,
  soldNum,
  originalPrice,
  skuId,
  saleNum,
  virtualType,
  salePriceAfterDeduction,
  pointsDeduction,
  itemId,
  promotionInfos,
  salePrice,
}) => ({
  title: itemName,
  subtitle: originalPrice ? `价值${originalPrice}元` : '',
  amount: virtualSalePrice,
  image: listItemUrl,
  soldNum,
  cost: virtualSalePrice || 0,
  price: originalPrice || 0,
  skuId,
  saleNum,
  virtualType,
  salePriceAfterDeduction: salePriceAfterDeduction || 0,
  pointsDeduction: pointsDeduction || 0,
  salePrice,
  itemId,
  promotionInfos: promotionInfos && promotionInfos.map((item) => ({
    ...item,
    worth: item.deductionAmount,
  })),
});

export default {
  components: {
    Empty,
    ExchangePopup,
    ExchangeItemCard,
    ExchangeSuccessPopup,
    HighlightText,
  },
  props: {
    bounty: {
      type: Number,
      default: 0,
    },
    points: {
      type: Number,
      required: true,
    },
    tabs: {
      type: Array,
      default: () => TABS,
    },
    popupSource: {
      type: Number,
      required: true,
    },
    hideBonus: {
      type: Boolean,
      default: false,
    },
    moduleConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  inject: {
    getStyleVersion: { default: () => null },
  },
  data() {
    return {
      pageSize: 8,
      pageNums: [],
      active: 0,
      info: [],
      hasMore: [],
      loading: false,
      // 当前兑换信息
      currentExchangeInfo: {},
      // 兑换弹窗是否显示
      exchangePopupVisible: false,
      // 是否展示积分+现金组合支付
      isShowGroupPayment: false,
      // 兑换成功弹窗是否显示
      exchangeSuccessPopupVisible: false,
      // 兑换成功商品信息
      goodsInfo: {},
      firstLoad: true,
      // 可配置副标题文本样式（统一常量）
      subtitleNormalStyle: SUBTITLE_NORMAL_STYLE,
      subtitleHighlightStyle: SUBTITLE_HIGHLIGHT_STYLE,
    };
  },
  computed: {
    // 金币图标（固定使用新版）
    coinIcon() {
      return 'https://m.hellobike.com/fd018348_new_coin.png';
    },
    // 当前 tab 名称
    currentTabName() {
      return this.tabs?.[this.active]?.name || '';
    },
    current() {
      return {
        ...(this.tabs[this.active] || {}),
        pageNum: this.pageNums[this.active] || 1,
      };
    },
    items() {
      return this.info[this.active] || [];
    },
    finishedtext() {
      if (!this.items || this.items.length === 0) return '';
      return this.hasMore[this.active] ? '' : '到底了';
    },
    // 无数据且不处于加载状态
    empty() {
      return (!this.items || this.items.length === 0) && !this.loading;
    },
  },
  watch: {
    tabs(newVal, oldVal) {
      if (newVal && newVal.length > 0 && newVal.length !== oldVal.length) {
        this.hasMore = new Array(newVal.length).fill(true);

        // 处理路由参数定位 tab（异步场景）
        const { anchorModule, tab } = this.$route.query;
        if (anchorModule === FULI_MODULE.welfare_bountyExchange && tab) {
          const idx = newVal.findIndex((i) => i.type === tab);
          if (idx !== -1) {
            this.active = idx;
          }
        }

        // 触发数据加载
        this.getData();
      }
    },
    points() {
      this.refreshList();
    },
  },
  created() {
    if (this.tabs && this.tabs.length > 0) {
      this.hasMore = new Array(this.tabs.length).fill(true);

      // 获取当前选中的tab
      const { anchorModule, tab } = this.$route.query;
      if (anchorModule === FULI_MODULE.welfare_bountyExchange && tab) {
        const idx = this.tabs.findIndex((i) => i.type === tab);
        if (idx !== -1) {
          this.active = idx;
        }
      }
    }

    // 获取兑换商品列表
    this.getData();
  },
  methods: {
    onLoad() {
      if (this.loading) return;
      if (this.hasMore[this.active]) {
        this.getData();
      }
    },
    moduleExpose() {
      // 商品兑换模块曝光
      SPM.moduleExpose({
        moduleId: 'Welfare_goods',
        contentId: 'Welfare_goods',
        businessInfo: {
          tab_name: this.tabs.map((t) => t.name).join(','),
          type_tab: this.tabs?.[this.active]?.name || '',
        },
      });
    },
    itemExpose(_entry, { item, index } = {}) {
      // 兑换商品曝光
      SPM.moduleExpose({
        moduleId: 'Welfare_goods_sku',
        contentId: 'Welfare_goods_sku',
        businessInfo: {
          tab_name: this.currentTabName,
          rankNum: index + 1,
          sku_name: item.title,
          skuId: item.skuId,
          exchangeNeedCash: item.salePriceAfterDeduction,
          exchangebonus: item.pointsDeduction,
          exchangeTotalBonus: item.cost,
          isSupportCash: !!(item.pointsDeduction && item.salePriceAfterDeduction),
        },
      });
    },
    getData(...args) {
      getExchangeList(mapper)
        .call(this, ...args)
        .finally(() => {
          this.$emit('loaded');
        });
    },
    // 重置并刷新商品列表
    refreshList() {
      this.info = [];
      this.pageNums = [];
      this.hasMore = new Array(this.tabs.length).fill(true);
      this.getData();
    },
    goDetail(item, index) {
      if (item) {
        // 兑换商品点击埋点上报
        SPM.clickButton({
          buttonName: 'Welfare_goods_sku',
          businessInfo: {
            tab_name: this.currentTabName,
            rankNum: index + 1,
            sku_name: item.title,
            isSupportCash: !!(item.pointsDeduction && item.salePriceAfterDeduction),
            exchangeTotalBonus: item.cost,
            skuId: item.skuId,
            exchangebonus: item.pointsDeduction,
            exchangeNeedCash: item.salePriceAfterDeduction,
          },
        });
        jumpUrl(
          `https://m.hellobike.com/AppPlatformH5/${config.env !== 'pro' ? `${config.env}/` : ''
          }latest/pr_index_bounty_detail_default.html#/bounty/detail/${item.skuId
          }?adsource=4${this.hideBonus ? '&hideBonus=true' : ''}`,
        );
      }
    },
    onExchange(item) {
      this.currentExchangeInfo = item;
      SPM.clickButton({
        buttonName: 'Welfare_goods_sku_button',
        businessInfo: {
          sku_name: item.title,
        },
      });

      if (item.cost <= this.points) {
        // 积分足够，纯积分兑换
        this.isShowGroupPayment = false;
        this.exchangePopupVisible = true;
      } else if (!this.hideBonus && item.salePriceAfterDeduction > 0) {
        // 积分不足但支持现金补差额（旧版不展示组合支付）
        this.isShowGroupPayment = true;
        this.exchangePopupVisible = true;
      } else {
        Toast.show('奖励金不足，快去赚币吧');
      }
    },
    changeTab(index) {
      if (this.loading) return;
      SPM.clickButton({
        buttonName: 'Welfare_goods_tab',
        businessInfo: {
          clickTab: this.tabs[index].name,
          tab_name: this.tabs.map((t) => t.name).join(','),
          type_tab: this.tabs[this.active]?.name || '',
        },
      });
      this.active = index;
      if (this.info && this.info[index] === undefined && this.hasMore[index]) {
        this.getData();
      }
    },
    // 跳转优惠券
    goMyCoupon() {
      SPM.clickButton({
        buttonName: 'Welfare_goods_coupon',
        businessInfo: {},
      });
      welfareGoCoupon();
    },
    // 关闭兑换弹窗
    handleExchangePopupClose() {
      this.exchangePopupVisible = false;
    },
    // 确认兑换成功
    handleExchangeSuccess() {
      this.handleExchangePopupClose();
    },
    // 展示兑换成功弹窗
    handleShowExchangeSuccessPopup() {
      this.exchangeSuccessPopupVisible = true;
    },
    // 关闭兑换成功弹窗
    handleExchangeSuccessPopupClose() {
      this.exchangeSuccessPopupVisible = false;
    },
    // 兑换成功商品信息点击
    handleExchangeGoodsInfoClick() {
      this.handleExchangeSuccessPopupClose();
      welfareGoCoupon();
      SPM.clickButton({
        buttonName: 'Welfare_skuduihuanpop_chakan',
        businessInfo: {},
      });
    },
    // 去赚币
    handleEarnPoints(num) {
      this.$emit('earnPoints', num);
    },
  },
};
</script>

<style lang="less" scoped>
.exchange-container {
  padding: 0.16rem 0 0;
  margin-top: 0.12rem !important;
  background: linear-gradient(180deg, #fff 0%, #fcfcfd 81.25%, #f0f3f5 100%);
  border-radius: 0.16rem 0.16rem 0 0;
  background-size: 100% 2.28rem;
  background-repeat: no-repeat;

  .exchange-header {
    position: relative;
    display: flex;
    justify-content: space-between;
    padding: 0 0.16rem 0 0.16rem;
    z-index: 1;

    .header-left {
      display: flex;
      align-items: center;
      max-width: 2.57rem;
    }

    .header-title {
      font-weight: 600;
      font-size: 0.15rem;
      color: #242729;
      flex: 0 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .header-subtitle {
      display: flex;
      align-items: center;
      margin-left: 0.08rem;
      flex: 0 1 auto;
      min-width: 0;

      .subtitle-logo {
        flex: none;
        width: 0.16rem;
        height: 0.16rem;
        margin-right: 0.02rem;
      }

      .subtitle-text {
        flex: 0 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .my-coupon-btn {
      display: flex;
      align-items: center;
      font-size: 0.12rem;
      line-height: normal;
      color: #879099;
    }

    .arrow-icon {
      width: 0.12rem;
      height: 0.12rem;
      vertical-align: bottom;
    }
  }

  .exchange-tabs {
    margin-top: 0.19rem;

    .tabs {
      width: 100%;
      height: 0.3rem;
      overflow-x: scroll;
      overflow-y: visible;
      white-space: nowrap;
      padding: 0 0.19rem;
      margin-bottom: 0.08rem;

      &::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
        color: transparent;
      }

      .tab {
        flex: none;
        display: inline-block;
        font-size: 0.16rem;
        position: relative;
        color: #5a6066;
        font-weight: 400;

        &:not(:last-of-type) {
          margin-right: 0.18rem;
        }

        &.active {
          color: #ff5729;
          font-weight: 600;
        }

        &::after {
          content: '';
          display: block;
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          bottom: -0.04rem;
          width: 0;
          height: 0.03rem;
          background-color: #ff5729;
          border-radius: 0.03rem;
          transition: width 0.2s ease-in-out;
        }

        &.active::after {
          width: 0.16rem;
        }
      }
    }
  }

  .exchange-content {
    min-height: calc(100vh - 1.2rem);

    .exchange-items {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      padding: 0 0.12rem;
    }

    .exchange-item-card-area {
      margin-bottom: 0.1rem;
    }
  }

  .more {
    margin: 0.15rem auto 0.05rem auto;
    color: #5a6066;
    background-color: #f0f3f5;
    border-radius: 0.32rem;
    width: 1.43rem;
    height: 0.26rem;
    line-height: 0.26rem;
    font-size: 0.12rem;

    * {
      border-color: #999 !important;
    }

    .more-arrow-icon {
      position: relative;
      top: -0.02rem;
    }
  }

  .empty {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-size: 0.12rem;
    color: #ccc;
    padding-top: 0.6rem;
    padding-bottom: 0.2rem;

    img {
      width: 0.78rem;
      height: 0.68rem;
      margin-bottom: 0.08rem;
    }
  }
}
</style>
